import styles from './generalFormBuilder.module.scss';
import { Editor, Frame, SerializedNodes, useEditor } from '@craftjs/core';
import { FC, memo, PropsWithChildren, useEffect } from 'react';
import { Loading } from 'uikit/suspense/Loading';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { LeftBar, RightBar, TopBar } from './layout';
import { FormBuilderBufferContextProvider } from '../context/formBuilderBuffer.provider';
import { AttachmentsContextProvider } from '../context/attachments.provider';
import { ScoreContextProvider } from '../context/score.provider';
import { RenderNode } from './layout/renderNode/renderNode';
import { GeneralFormBuilderProps } from './generalFormBuilder.t';

export const GeneralFormBuilder: FC<GeneralFormBuilderProps> = memo(
  ({
    onSave,
    onSaveAs,
    onDelete,
    defaultLayout,
    resolver,
    selectors,
    layerItem,
    loading,
    formConfigSettingPanel,
    initialScoreSections,
    initialVariables,
    uploadFileFunction,
    configSelectorComponent,
  }) => {
    return (
      <Loading
        loading={loading}
        renderChildren={true}
        containerClassName={styles.loaderWrapper}
        testId={'general-form-builder'}
      >
        <FormBuilderBufferContextProvider>
          <ScoreContextProvider initialScoreSections={initialScoreSections} initialVariables={initialVariables}>
            <Editor
              resolver={resolver}
              enabled={true}
              onRender={RenderNode}
              indicator={{
                success: '#09a07c',
                error: '#e75223',
                thickness: 4,
                transition: 'unset',
              }}
            >
              <AttachmentsContextProvider uploadFileFunction={uploadFileFunction} initialAttachments={{}}>
                <div className={styles.builderContainer}>
                  <TopBar
                    onSave={onSave}
                    onSaveAs={onSaveAs}
                    onDelete={onDelete}
                    configSelectorComponent={configSelectorComponent}
                  />
                  <BuilderLayout>
                    <LeftBar selectors={selectors} layerItem={layerItem} />
                    <FrameWrapper data={defaultLayout} />
                    <RightBar formConfigSettingPanel={formConfigSettingPanel} />
                  </BuilderLayout>
                </div>
              </AttachmentsContextProvider>
            </Editor>
          </ScoreContextProvider>
        </FormBuilderBufferContextProvider>
      </Loading>
    );
  },
);

// The data prop on the Frame component in memorized after the initial render and cannot be changed.
// Use actions.deserialize(yourState) from useEditor instead
const FrameWrapper: FC<{ data: string | SerializedNodes }> = memo(({ data }) => {
  const { actions, connectors } = useEditor();
  useEffect(() => {
    actions.deserialize(data);
    actions.history.clear();
  }, [actions, data]);

  return (
    <>
      <PanelResizeHandle className={styles.resizeHandle} />
      <Panel>
        <div
          data-test-id={'ui-editor-container'}
          className={styles.editorBodyDesigner}
          ref={(ref) => {
            if (!ref) return;
            connectors.select(connectors.hover(ref, ''), '');
          }}
        >
          <Frame data={data} />
        </div>
      </Panel>
      <PanelResizeHandle className={styles.resizeHandle} />
    </>
  );
});

const BuilderLayout: FC<PropsWithChildren> = ({ children }) => {
  return (
    <PanelGroup direction={'horizontal'} className={styles.editorDesigner}>
      {children}
    </PanelGroup>
  );
};
